import WebViewer, { WebViewerInstance } from "@pdftron/webviewer";
import type { Core } from "@pdftron/webviewer";
import { observer } from "mobx-react";
import { useEffect, useRef, useState } from "react";
import { Button, Splitter, Tooltip } from "antd";
import { MessageOutlined } from "@ant-design/icons";
import { projectsDataStore, userInfoStore } from "Stores";
import { contactsStore } from "Stores/ContactsStore";
import { apryseViewerStore } from "Stores/PdfViewerStore/ApryseViewerStore";
import modularUI from "./config/modularUI";
import { I18N_NS } from "Config/i18n";
import { useTranslation } from "react-i18next";
import { configureSignatureTool } from "./config/signatureTool";
import { enableReadOnlyMode } from "./config/readOnlyMode";
import { MistralChat } from "./MistralChat";
import { openIncludeAnnotationsModal } from "./config/includeAnnotationsModal";
import {
  canModifyAnnotationAsAuthor,
  createAnnotationAuthor,
  isValidAnnotationUserId,
  resolveAnnotationDisplayAuthor,
} from "./annotationAuthor";

let webViewerInstance: WebViewerInstance = null;

function setWebViewerInstance(instance: WebViewerInstance) {
  webViewerInstance = instance;
}

/**
 * Unmount has to be called because when viewer is opened next time, the annotations event listeners are not triggered.
 */
function unmountWebViewerInstance() {
  webViewerInstance?.Core.documentViewer.unmount();
  webViewerInstance = null;
}

export const ApryseViewer = observer(() => {
  const viewer = useRef<HTMLDivElement>(null);
  const { t } = useTranslation(I18N_NS.PROJECT);
  const [isChatOpened, setIsChatOpened] = useState(false);
  const [viewerSize, setViewerSize] = useState<number | string>("100%");
  const [chatSize, setChatSize] = useState<number | string>("0%");

  const toggleChat = () => {
    const newIsChatOpened = !isChatOpened;
    if (newIsChatOpened) {
      setViewerSize("70%");
      setChatSize("30%");
    } else {
      setViewerSize("100%");
      setChatSize("0%");
    }
    setIsChatOpened(!isChatOpened);
  };

  const handleResizeEnd = (sizes: number[]) => {
    setViewerSize(sizes[0]);
    setChatSize(sizes[1]);
  };

  useEffect(() => {
    if (viewer.current) {
      let cssLink: string;
      let pathLink: string;
      if (process.env.NODE_ENV === "development") {
        cssLink = "/lib/pdftron_custom.css";
        pathLink = "/pdf_viewer";
      } else {
        cssLink = `/${window.GLOBAL.ROUTER_BASE}/lib/pdftron_custom.css?r=${Date.now()}`;
        pathLink = `/${window.GLOBAL.ROUTER_BASE}/pdf_viewer`;
      }
      const isReadOnly =
        projectsDataStore.isArchived || userInfoStore.isGuest || !!apryseViewerStore.options.archivedFileId;
      const language = userInfoStore.language;

      const canDownload = apryseViewerStore.options.canDownload;
      const annotationAuthor = isValidAnnotationUserId(userInfoStore.id)
        ? createAnnotationAuthor(userInfoStore.id)
        : "";
      if (!annotationAuthor && !isReadOnly) {
        throw new Error("A valid project user id is required to edit annotations");
      }

      WebViewer(
        {
          path: pathLink,
          css: cssLink,
          licenseKey: import.meta.env.VITE_APRYSE_LICENSE_KEY,
          defaultLanguage: language,
          annotationUser: annotationAuthor,
          enableFilePicker: false,
          enableRedaction: false,
          filename: apryseViewerStore.fileName,
          isAdminUser: false,
          isReadOnly: isReadOnly,
        },
        viewer.current
      ).then(async (instance) => {
        const { annotationManager } = instance.Core;
        annotationManager.setCurrentUser(annotationAuthor);
        annotationManager.setAnnotationDisplayAuthorMap((author) =>
          resolveAnnotationDisplayAuthor({
            author,
            currentUserId: userInfoStore.id,
            currentUserName: contactsStore.getUserNameById(userInfoStore.id, false) || userInfoStore.name,
            getUserNameById: (userId) => contactsStore.getUserNameById(userId, false),
            fallbackUserName: t("[Removed user]", { ns: I18N_NS.GENERAL }),
          })
        );
        annotationManager.setPermissionCheckCallback((author: string) =>
          canModifyAnnotationAsAuthor(author, annotationAuthor)
        );

        const customDownload = async () => {
          if (apryseViewerStore.hasAnnotations) {
            openIncludeAnnotationsModal({
              instance,
              title: t("Download"),
              confirmText: t("Download"),
              dataElement: "myCustomModal-download",
              originalFileLabel: t("original File"),
              withAnnotationsLabel: t("PDF with Annotations"),
              onConfirm: async (includeAnnotations: boolean) => {
                await apryseViewerStore.downloadFile({ includeAnnotations });
              },
            });
          } else {
            await apryseViewerStore.downloadFile({ includeAnnotations: false });
          }
        };

        const customPrint = async () => {
          if (apryseViewerStore.hasAnnotations) {
            openIncludeAnnotationsModal({
              instance,
              title: t("Print"),
              confirmText: t("Print"),
              dataElement: "myCustomModal-print",
              originalFileLabel: t("original File"),
              withAnnotationsLabel: t("PDF with Annotations"),
              onConfirm: async (includeAnnotations: boolean) => {
                await apryseViewerStore.printPdf({ includeAnnotations });
              },
            });
          } else {
            await apryseViewerStore.printPdf({ includeAnnotations: false });
          }
        };

        const functionMap = {
          customDownload,
          customPrint,
        };
        const modularConfig = JSON.parse(JSON.stringify(modularUI));
        if (canDownload) {
          modularConfig.modularComponents.downloadButton.disabled = false;
          modularConfig.modularComponents.printButton.disabled = false;
        }
        instance.UI.importModularComponents(modularConfig, functionMap);

        // Delete link feature from popups
        instance.UI.textPopup.update(
          instance.UI.textPopup.getItems().filter((item: { dataElement: string }) => item.dataElement !== "linkButton")
        );
        instance.UI.annotationPopup.update(
          instance.UI.annotationPopup
            .getItems()
            .filter(
              (item: { dataElement: string }) =>
                item.dataElement !== "linkButton" && item.dataElement !== "linkAnnotationUnlinkButton"
            )
        );
        instance.UI.contextMenuPopup.update(
          instance.UI.contextMenuPopup
            .getItems()
            .filter((item: { dataElement: string }) => item.dataElement !== "linkButton")
        );
        instance.UI.setLanguage(language);

        instance.UI.disableTools([
          instance.Core.Tools.ToolNames.CROP,
          instance.Core.Tools.ToolNames.SNIPPING,
          instance.Core.Tools.ToolNames.REDACTION,
        ]);

        // Delete link feature from popups
        instance.UI.textPopup.update(
          instance.UI.textPopup.getItems().filter((item: { dataElement: string }) => item.dataElement !== "linkButton")
        );
        instance.UI.annotationPopup.update(
          instance.UI.annotationPopup
            .getItems()
            .filter(
              (item: { dataElement: string }) =>
                item.dataElement !== "linkButton" && item.dataElement !== "linkAnnotationUnlinkButton"
            )
        );
        instance.UI.contextMenuPopup.update(
          instance.UI.contextMenuPopup
            .getItems()
            .filter((item: { dataElement: string }) => item.dataElement !== "linkButton")
        );
        instance.UI.setLanguage(userInfoStore.language);

        configureSignatureTool(instance, userInfoStore.id);

        const file = await apryseViewerStore.fetchPdfFile();
        instance.UI.loadDocument(file, {
          filename: apryseViewerStore.fileName,
          password: apryseViewerStore.password,
          extension: "pdf",
        });

        const checkHasAnnotations = () => {
          const annotations = annotationManager.getAnnotationsList().filter((annotation) => !annotation.isInternal());
          if (annotations.length > 0) {
            apryseViewerStore.setHasAnnotations(true);
          } else {
            apryseViewerStore.setHasAnnotations(false);
          }
        };

        const annotationsXml = await apryseViewerStore.getAnnotationsXml();
        instance.Core.documentViewer.addEventListener("documentLoaded", async () => {
          if (annotationsXml) {
            await annotationManager.importAnnotations(annotationsXml);
            checkHasAnnotations();
          }
          annotationManager.addEventListener(
            "annotationChanged",
            async (annotations: Core.Annotations.Annotation[], action, { imported, autoFocus }) => {
              if (imported) {
                // annotations that are within PDF should be read only
                annotations.forEach((annotation) => {
                  annotation.ReadOnly = true;
                  annotation.RotationControlEnabled = false;
                });
                return;
              }
              if (!annotations.length) {
                return;
              }
              if (isReadOnly) {
                // The measure tools stay available in archived projects and older file versions,
                // but nothing drawn there may be stored.
                return;
              }
              let xfdfString: string = "";
              try {
                xfdfString = await annotationManager.exportAnnotations({
                  annotationList: annotations,
                  links: true,
                  fields: true,
                });
              } finally {
                checkHasAnnotations();
              }
              await apryseViewerStore.changeAnnotations(xfdfString, action);
            }
          );
          // FIXME: https://community.apryse.com/t/annotations-are-not-interactive-on-subsequent-webviewer-initializations/12158/3
          // temporary workaround to make annotations interactive on subsequent webviewer initializations
          // while apryse is fixing the issue
          instance.Core.documentViewer.addEventListener("click", (e) => {
            const annots = instance.Core.annotationManager.getAnnotationsByMouseEvent(e, true);
            instance.Core.annotationManager.selectAnnotations(annots);
          });
        });
        if (isReadOnly) {
          enableReadOnlyMode(instance);
        }

        // For debugging purposes
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        // (window as any).WebViewer = instance;
        setWebViewerInstance(instance);
      });
    }
    return () => {
      unmountWebViewerInstance();
    };
  }, []);

  return (
    <>
      <Splitter lazy style={{ height: "100%", overflow: "hidden" }} onResizeEnd={handleResizeEnd}>
        <Splitter.Panel size={viewerSize} style={{ position: "relative" }}>
          <div className="webviewer" ref={viewer} style={{ height: "100%", overflow: "hidden" }}></div>
          {projectsDataStore.functions?.includes("AI_CHAT") && (
            <Tooltip title={t("Ask our PAVE AI assistant")} placement="left">
              <Button
                type="primary"
                shape="circle"
                icon={<MessageOutlined />}
                onClick={toggleChat}
                style={{
                  position: "absolute",
                  bottom: 5,
                  right: 25,
                  zIndex: 1000,
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                }}
                aria-label="Open AI Chat"
              />
            </Tooltip>
          )}
        </Splitter.Panel>
        {isChatOpened && (
          <Splitter.Panel size={chatSize}>
            <MistralChat fileId={apryseViewerStore.fileId} />
          </Splitter.Panel>
        )}
      </Splitter>
    </>
  );
});
